<template>
  <div :id="content.idHTML" :class="jel_getClassObject(content)">
    <!-- Texte du haut pour promo non valide ou expirée (Activé par SET_IS_PROMO_NOT_VALID dans le store) -->
    <p v-if="isPromoNotValid" class="promo-warning-top" v-html="texteHautPromoExpireeBienvenue" />

    <p
      v-else
      class="sousTitre text-center texte-padding mt-0 px-1"
      label-for="offreBienvenue"
      v-html="texteOffreBienvenue"
    />

    <b-form-radio-group id="offre" plain name="choixOffreBienvenue" class="text-center">
      <EditableArea
        v-if="hasInfo"
        :content="content.formulaire"
        :parentTemplateId="metadata['mgnl:template']"
      />
    </b-form-radio-group>
    <div class="text-center">
      <p
        v-if="texteRegleOffreBienvenue"
        class="prefix-modalites"
        v-html="texteRegleOffreBienvenue"
      />
      <Hyperlien class="pt-1" :content="getContentHyperlien" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import store from '@/store';
import { EditableArea } from '@/vue-editor';
import { bus } from '@/main';

// Helpers
import { getPromoFromQueryParams, hasPromoSelected, inMgnl } from '@/helpers/AppHelpers';

import { gtmCustomEventByVendor } from '@/helpers/ConsentHelpers';
import {
  containsProductSource,
  getProductSourceFromQueryParams,
} from '@/helpers/ProductSourceHelpers';

// Components
import Hyperlien from '@/components/base/Hyperlien';

// Services
import promo from '@/services/api/compte/promo';

export default {
  name: 'FormulaireOffreBienvenue',
  components: {
    EditableArea,
    Hyperlien,
  },
  props: {
    metadata: { type: Object, required: true },
    content: { type: Object, required: true },
  },
  i18n: {
    messages: {
      fr: { messageComponent: { poid: 'Ko' } },
      en: { messageComponent: { poid: 'KB' } },
    },
  },
  data() {
    return {
      listePromoAcquisition: null,
      method: 'default',
      position: 1,
      promoChoisie: '',
      textePromoChoisie: store.state.promo.libellePromo || '',
      uniqueId: this.uid,
    };
  },
  computed: {
    ...mapState('promo', ['isPromoNotValid']),
    hasInfo() {
      return this.content.formulaire || inMgnl;
    },
    getContentHyperlien() {
      const lienRegleOffreBienvenue = this.getLienRegleOffreBienvenue();

      // Construire et retourner l'objet du lien si les métadonnées sont disponibles
      if (lienRegleOffreBienvenue?.lienInterneAsset?.metadata) {
        // Calculer le poids du fichier
        const poidFichier = this.calculateFileSize(lienRegleOffreBienvenue.lienInterneAsset);
        // Déterminer le texte du lien
        const texte = poidFichier
          ? `${this.content.libLienRegleOffreBienvenue} [PDF,&nbsp;${poidFichier}&nbsp;${this.$t(
              'messageComponent.poid'
            )}]`
          : this.content.libLienRegleOffreBienvenue;
        // Retourner le lien
        return {
          type: 'lienAsset',
          lien: {
            field: 'lienInterneAsset',
            lienInterneAsset: lienRegleOffreBienvenue.lienInterneAsset,
            cacherPoidsFichier: 'false',
          },
          texte,
          classeCSSLien: 'lien-offre',
        };
      }
      // Retour par défaut si aucune métadonnée
      return this.content.lienReglesOffreInscription;
    },
    texteOffreBienvenue() {
      return this.content.libOffreBienvenue;
    },
    texteHautPromoExpireeBienvenue() {
      return this.content.libPromoExpTopBienvenue;
    },
    texteRegleOffreBienvenue() {
      return this.content.libTexteRegleOffreBienvenue;
    },
    positionOffre() {
      const mappingPromo = {
        [this.listePromoAcquisition?.LOTERIES?.idob]: 1,
        [this.listePromoAcquisition?.CED?.idob]: 2,
        [this.listePromoAcquisition?.SPORT?.idob]: 3,
        [this.listePromoAcquisition?.CASINO?.idob]: 4,
      };

      return mappingPromo[this.promoChoisie] || 1;
    },
  },

  async mounted() {
    const codePromo = getPromoFromQueryParams();
    const codeProductSource = getProductSourceFromQueryParams();

    store.commit('compte/SET_PRODUCT_SOURCE', null);
    if (containsProductSource) {
      store.commit('compte/SET_PRODUCT_SOURCE', codeProductSource);
    }

    if (codePromo && !hasPromoSelected()) {
      store.commit('compte/SET_CODE_PROMO', null);
      return;
    }

    // Récupértation des informations sur les promotions d'acquisition
    this.listePromoAcquisition = await promo.getInfoAppPromo('ACQUISITION');
    // Définir la promo choisie depuis le store ou par défaut (Loteries)
    const codePromoStore = store.state.compte.codePromo;
    this.promoChoisie = this.listePromoAcquisition?.LOTERIES?.idob;
    /**
     * Si le code promo est dans les QueryParam
     * et l'offre sélectionnée (QueryParam 'os=1')
     * est dans la liste des Promos d'acquisition
     * Alors, on sélectionne la carte de l'offre passée en paramètre
     * Sinon, on sélectionne l'offre Loteries par défaut
     */
    if (codePromo && hasPromoSelected()) {
      const promoKey = this.listePromoAcquisition['@nodes'].find(
        (promo) => codePromo === this.listePromoAcquisition[promo]?.idob
      );
      // La promo passée en paramètre est dans la liste des promos d'acquisition
      if (promoKey) {
        this.promoChoisie = codePromo;
      }
    }
    this.updatePromoChoisie(this.promoChoisie, this.method);

    // Récupération de la promo via l'événement bus
    bus.$on('codePromo', this.updatePromoChoisie);
  },
  methods: {
    calculateFileSize(lienInterneAsset) {
      return lienInterneAsset?.metadata?.fileSize
        ? (lienInterneAsset.metadata.fileSize / 1000).toFixed(0)
        : null;
    },
    // Récupère le lien des règles d'offre en fonction du code promo
    getLienRegleOffreBienvenue() {
      if (!this.listePromoAcquisition) {
        return this.content.lienReglesOffreInscription;
      }

      const promoKey = this.listePromoAcquisition['@nodes'].find(
        (promo) => this.promoChoisie === this.listePromoAcquisition[promo]?.idob
      );

      return promoKey
        ? this.content[`lienReglesOffre${promoKey}`]
        : this.content.lienReglesOffreInscription;
    },
    // Vérifie que le promo est dans la liste des promos d'acquisition
    isCodePromoAcquisition(codePromo) {
      return (
        this.listePromoAcquisition?.['@nodes']?.some(
          (promo) => codePromo === this.listePromoAcquisition[promo]?.idob
        ) || false
      );
    },
    // Méthode pour mettre à jour la promo choisie dans le store
    updatePromoChoisie(value, method) {
      this.promoChoisie = value;
      store.commit('compte/SET_CODE_PROMO', this.promoChoisie);
      store.commit('promo/SET_LIEN_MODALITES', this.getContentHyperlien);
      // Requis pour envoyer au dataLayer GA4
      this.method = method || 'select';
      store.commit('compte/SET_METHODE_CHOIX_PROMO', this.method);
      store.commit('compte/SET_POSITION_PROMO', this.positionOffre);
    },
  },
};
</script>

<style scoped>
.promo-warning-top {
  padding: 0 1.5rem;
}
.promo-warning-bottom {
  padding: 1rem 1.5rem 0;
}
.prefix-modalites {
  margin-top: 20px;
  margin-bottom: 0;
  font-size: 13px;
  line-height: 17px;
  letter-spacing: 0px;
  color: #fff;
  text-align: center;
}
.prefix-modalites + .pt-1 {
  padding-top: 0 !important;
}
</style>
